import React, { useState, useMemo } from 'react';
import { Formik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import './TestDrive.scss';
import FormHeader from '../FormHeader/FormHeader';
import Recaptcha from '../Recaptcha';
import Button from '../Button';
import FormField from '../FormField';
import LocalSkeleton from '../Skeleton';
import { DEFAULT_COLOR, SUCURSALES } from '../../utils/constants';
import { uniqBy } from 'lodash';
import { IsOnlyNumbers } from '../../utils/helpers';
import { useUserData } from '../../utils/hooks';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const Loader = () => (
  <div className="test-drive-skeleton">
    <LocalSkeleton>
      <div className="test-drive-skeleton__section">
        <div className="test-drive-skeleton__title skeleton__box" />
        <div className="test-drive-skeleton__row">
          <div className="test-drive-skeleton__field skeleton__box" />
          <div className="test-drive-skeleton__field skeleton__box" />
        </div>
        <div className="test-drive-skeleton__row">
          <div className="test-drive-skeleton__field skeleton__box" />
          <div className="test-drive-skeleton__field skeleton__box" />
        </div>
      </div>
      <div className="test-drive-skeleton__section">
        <div className="test-drive-skeleton__title skeleton__box" />
        <div className="test-drive-skeleton__row">
          <div className="test-drive-skeleton__field skeleton__box" />
          <div className="test-drive-skeleton__field skeleton__box" />
        </div>
        <div className="test-drive-skeleton__row">
          <div className="test-drive-skeleton__field skeleton__box" />
          <div className="test-drive-skeleton__field skeleton__box" />
        </div>
      </div>
    </LocalSkeleton>
  </div>
);

const TestDrive = ({
  logoUrl,
  car,
  isLoading,
  testDriveInfo,
  requestTestDrive,
}) => {
  const history = useHistory();
  const [hideId] = useState(false);
  const { saveUserData, userData, getClientInfoRequest, gometa } =
    useUserData();
  const [passedCaptcha, setPassedCaptcha] = useState(false);
  const marcas = testDriveInfo?.data?.marcas;
  const marcasOptions = useMemo(
    () =>
      (marcas &&
        Object.keys(marcas).map((key) => ({
          label: marcas[key],
          value: marcas[key],
        }))) ||
      [],
    [marcas]
  );

  const getClientInfo = (dat, setField) => {
    getClientInfoRequest(dat, ({ data }) => {
      if (!data || !data?.length) {
        return;
      }
      const d = data[0];
      if (d?.guess_type === 'DIMEX/NITE') {
        setField('name_client', d.firstname);
        setField('last_name', d.lastname);
        setField('type_id', 'DIMEX');
      } else if (d?.guess_type !== 'FISICA') {
        setField('name', d.fullname);
        setField('last_name', 'N/A');
        setField('type_id', 'JURIDICO');
      } else {
        setField('name', d.firstname);
        setField('last_name', d.lastname);
        setField('type_id', 'FISICO');
      }
    });
  };

  if (testDriveInfo?.confirmation?.requestSent) {
    history.push(
      `/test-drive/reserva/${testDriveInfo?.confirmation?.data?.cliente?.identificacion}-${testDriveInfo?.confirmation?.data?.id}`
    );
  }
  return (
    <div
      className="test-drive gradient gradient--full"
      style={{ '--color': car?.colorCode || DEFAULT_COLOR }}
    >
      <Helmet>
        <meta
          name="description"
          content="Solicitá hoy mismo tu test drive en Veinsa Motors y descubrí por qué nuestras marcas son las mejores. "
        />
      </Helmet>
      <FormHeader
        isLoading={isLoading}
        logoUrl={logoUrl}
        sectionHeader="ingresá tus datos para comprar tu chuzo."
        headerText="Solicitar mi test drive"
        headerSubText="Probá tu próximo chuzo…"
        bottomBorder
      />
      <div className="purchase__content classic-form test-drive__content">
        {/* <div className="test-drive__aside">
          <div className="test-drive__image">
            {isLoading ? (
              <Skeleton circle={true} height={200} width={200} />
            ) : (
              <img src={imageUrl || carPlaceholder} alt="Vehiculo" />
            )}
          </div>
          <h4 className="test-drive__mobile-header mobile">
            Ingresá tus datos para solicitar tu test drive
          </h4>
        </div> */}
        <div className="test-drive__form">
          {isLoading ? (
            <Loader />
          ) : (
            <Formik
              enableReinitialize
              initialValues={{
                marca: car?.marca || '',
                modelo: car?.modelo || '',
                transmision: car?.transmision || '',
                vehiculo: car?.codigoAnio || '',
                name: userData.name,
                last_name: userData.lastName,
                type_id: hideId ? '' : userData.typeId || 'FISICO',
                id_real: hideId ? '' : userData.id,
                email: userData.email,
                phone: userData.phone,
                direccion: 'n/a',
                profesion: 'n/a',
                date_rvsp: '',
                time: '',
                sucursal: '',
                additional_info: '',
                politica: 'politica',
                terminos: false,
              }}
              validationSchema={Yup.object().shape({
                marca: Yup.string().required('Requerido'),
                modelo: Yup.string().required('Requerido'),
                transmision: Yup.string().required('Requerido'),
                vehiculo: Yup.string().required('Requerido'),
                name: Yup.string().required('Requerido'),
                last_name: Yup.string().required('Requerido'),
                type_id: !hideId
                  ? Yup.string().required('Requerido')
                  : Yup.string(),
                id_real: Yup.number()
                  .integer()
                  .test(
                    'len',
                    'Ingrese un formato correcto con solo números',
                    (val) => {
                      if (hideId) {
                        return true;
                      }
                      const hasOnlyNumbers = IsOnlyNumbers(val);
                      if (!hasOnlyNumbers) {
                        return false;
                      }
                      return (
                        !!val &&
                        val.toString().length <= 12 &&
                        val.toString().length > 8
                      );
                    }
                  ),
                email: Yup.string()
                  .required('Requerido')
                  .email('El email es inválido'),
                phone: Yup.number()
                  .integer()
                  .test(
                    'len',
                    'El télefono debe contener 8 digitos y solo números',
                    (val) => {
                      const hasOnlyNumbers = IsOnlyNumbers(val);
                      if (!hasOnlyNumbers) {
                        return false;
                      }
                      return !!val && val.toString().length === 8;
                    }
                  ),
                direccion: Yup.string(),
                profesion: Yup.string().required('Requerido'),
                date_rvsp: Yup.string().required('Requerido'),
                time: Yup.string().required('Requerido'),
                sucursal: Yup.string().required('Requerido'),
                additional_info: Yup.string(),
                politica: Yup.string().required('Requerido'),
                // terminos: Yup.string().required('Requerido'),
              })}
              onSubmit={(values) => {
                saveUserData({
                  email: values.email,
                  name: values.name,
                  phone: values.phone,
                  lastName: values.last_name,
                  typeId: values.type_id,
                  id: values.id_real,
                  profession: values.profesion,
                  address: values.direccion,
                  typeContract: userData?.typeContract,
                  typeContact: userData?.typeContact,
                });
                requestTestDrive({
                  ...values,
                  time: values.time.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                  }),
                  date_rvsp: moment(values.date_rvsp).format(
                    'YYYY-MM-DD 00:00:00'
                  ),
                });
              }}
            >
              {(formik) => (
                <form onSubmit={formik.handleSubmit}>
                  <div className="classic-form__header">
                    <div className="classic-form__header-info">
                      <h4 className="classic-form__mobile-header classic-form__desktop-header desktop ">
                        INGRESÁ TUS DATOS PARA SOLICITAR TU TEST DRIVE
                      </h4>
                      <h3 className="classic-form__subheader">
                        Datos personales
                      </h3>
                      {!hideId && (
                        <div className="form-field">
                          <label className="form-field__label">
                            Para agilizar el proceso <strong>ingresá</strong> tu
                            número de identificación.
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="classic-form__header-img purchase__image">
                      {/* {isLoading ? (
                        <Skeleton circle={true} height={200} width={200} />
                      ) : (
                        <img src={imageUrl || carPlaceholder} alt="Vehiculo" />
                      )} */}
                    </div>
                  </div>

                  <br />
                  {!hideId && (
                    <div className="classic-form__row">
                      <div className="classic-form__field">
                        <FormField
                          label="Número de Identificación"
                          id="id_real"
                          name="id_real"
                          type="number"
                          onBlur={formik.handleBlur}
                          placeholder="000000000"
                          onChange={formik.handleChange}
                          value={formik.values.id_real}
                          touched={formik.touched.id_real}
                          error={formik.errors.id_real}
                          required
                          pattern="[0-9\]+"
                        />
                        {!gometa?.isLoading && (
                          <span className="waiting-message waiting-message--search">
                            <Button
                              button="button"
                              type="red"
                              onClick={(e) => {
                                e.preventDefault();
                                if (formik?.errors?.id_real) {
                                  return;
                                }
                                getClientInfo(
                                  formik?.values?.id_real,
                                  formik.setFieldValue
                                );
                              }}
                            >
                              Buscar
                            </Button>
                          </span>
                        )}
                        {gometa?.isLoading && (
                          <span className="waiting-message">
                            {' '}
                            Espera unos segundos y verifica tu información.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="classic-form__row">
                    <div className="classic-form__field">
                      <FormField
                        label="Nombre"
                        id="name"
                        name="name"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                        touched={formik.touched.name}
                        error={formik.errors.name}
                        required
                      />
                    </div>
                    <div className="classic-form__field">
                      <FormField
                        label="Apellidos"
                        id="last_name"
                        name="last_name"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.last_name}
                        touched={formik.touched.last_name}
                        error={formik.errors.last_name}
                        required
                      />
                    </div>
                    <div className="classic-form__field">
                      <FormField
                        label="Correo electrónico"
                        id="email"
                        name="email"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        touched={formik.touched.email}
                        error={formik.errors.email}
                        required
                      />
                    </div>
                    <div className="classic-form__field">
                      <FormField
                        label="Número de teléfono"
                        id="phone"
                        name="phone"
                        type="number"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone}
                        touched={formik.touched.phone}
                        error={formik.errors.phone}
                        required
                      />
                    </div>
                  </div>
                  {!hideId && (
                    <div className="classic-form__row">
                      <div
                        className="classic-form__field"
                        style={{ zIndex: 6 }}
                      >
                        <FormField
                          label="Tipo de Identificación"
                          id="type_id"
                          name="type_id"
                          type="select"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.type_id}
                          touched={formik.touched.type_id}
                          error={formik.errors.type_id}
                          options={[
                            { value: 'FISICO', label: 'Físico' },
                            { value: 'JURIDICO', label: 'Jurídico' },
                            { value: 'DIMEX', label: 'DIMEX' },
                            {
                              value: 'CUERPO DIPLOMATICO',
                              label: 'Cuerpo Diplomático',
                            },
                          ]}
                          required
                        />
                      </div>
                    </div>
                  )}
                  <h3 className="test-drive__subheader">
                    Selección de Vehículo
                  </h3>
                  <div className="classic-form__row">
                    <div className="classic-form__field" style={{ zIndex: 5 }}>
                      <FormField
                        label="Seleccione la marca"
                        id="marca"
                        name="marca"
                        type="select"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.marca}
                        touched={formik.touched.marca}
                        error={formik.errors.marca}
                        required
                        options={marcasOptions}
                        disabled={!!car}
                      />
                    </div>
                    <div className="classic-form__field" style={{ zIndex: 4 }}>
                      <FormField
                        label="Seleccione el modelo"
                        id="modelo"
                        name="modelo"
                        type="select"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.modelo}
                        touched={formik.touched.modelo}
                        error={formik.errors.modelo}
                        required
                        options={testDriveInfo?.data?.modelos
                          ?.filter((modelo) =>
                            formik.values.marca
                              ? (modelo.marca || '').toUpperCase() ===
                              formik.values.marca
                              : true
                          )
                          .map((modelo) => ({
                            label: modelo.modelo,
                            value: modelo.modelo,
                          }))}
                        disabled={!!car}
                      />
                    </div>
                    <div className="classic-form__field" style={{ zIndex: 3 }}>
                      <FormField
                        label="Transmisión"
                        id="transmision"
                        name="transmision"
                        type="select"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.transmision}
                        touched={formik.touched.transmision}
                        error={formik.errors.transmision}
                        required
                        options={uniqBy(
                          testDriveInfo?.data?.cars
                            ?.filter((car) =>
                              formik.values.modelo
                                ? (car.modelo || '').toUpperCase() ===
                                formik.values.modelo
                                : true
                            )
                            .map((modelo) => ({
                              label: modelo.transmision,
                              value: modelo.transmision,
                            })),
                          'value'
                        )}
                        disabled={!!car}
                      />
                    </div>
                    <div className="classic-form__field" style={{ zIndex: 2 }}>
                      <FormField
                        label="Versión del Vehículo"
                        id="vehiculo"
                        name="vehiculo"
                        type="select"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.vehiculo}
                        touched={formik.touched.vehiculo}
                        error={formik.errors.vehiculo}
                        required
                        options={
                          testDriveInfo?.data?.cars
                            ?.filter((version) =>
                              !formik.values.transmision
                                ? true
                                : version.transmision ===
                                formik.values.transmision
                            )
                            ?.filter((version) =>
                              !formik.values.modelo
                                ? true
                                : version.modelo === formik.values.modelo
                            )
                            .map((version) => ({
                              label: version.version,
                              value: version.codigo,
                            })) || []
                        }
                        disabled={!!car}
                      />
                    </div>
                  </div>
                  <h3 className="test-drive__subheader">
                    Fecha para tu Test drive
                  </h3>
                  <div className="classic-form__row">
                    <div className="classic-form__field">
                      <FormField
                        label="Día de preferencia"
                        id="date_rvsp"
                        name="date_rvsp"
                        type="date"
                        placeholder="Seleccionar fecha"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.date_rvsp}
                        touched={formik.touched.date_rvsp}
                        error={formik.errors.date_rvsp}
                        required
                        min={new Date()}
                      />
                    </div>
                    <div className="classic-form__field">
                      <FormField
                        label="Hora de preferencia"
                        id="time"
                        name="time"
                        type="time"
                        placeholder="Seleccionar hora"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.time}
                        touched={formik.touched.time}
                        error={formik.errors.time}
                        required
                      />
                    </div>
                    <div className="classic-form__field" style={{ zIndex: 1 }}>
                      <FormField
                        label="Sucursal de preferencia"
                        id="sucursal"
                        name="sucursal"
                        type="select"
                        placeholder="Seleccionar"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.sucursal}
                        touched={formik.touched.sucursal}
                        error={formik.errors.sucursal}
                        required
                        options={SUCURSALES}
                      />
                    </div>
                  </div>
                  <br />
                  <div className="classic-form__row">
                    <div className="classic-form__field classic-form__field--auto">
                      <FormField
                        label="Escribe alguna observación o detalle importante de tu reserva"
                        id="additional_info"
                        name="additional_info"
                        type="textarea"
                        placeholder="Escribe un comentario…"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.additional_info}
                        touched={formik.touched.additional_info}
                        error={formik.errors.additional_info}
                      />
                    </div>
                    <div className="classic-form__field classic-form__field--auto classic-form__field--politica">
                      <FormField
                        style={{ margin: '1.9rem 0' }}
                        url="/wp-content/themes/veinsa/files/politicas_de_privacidad.pdf"
                        label=""
                        id="politica"
                        name="politica"
                        type="radio"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.politica}
                        touched={formik.touched.politica}
                        error={formik.errors.politica}
                        options={[
                          {
                            component: (
                              <>
                                <div className="classic-form__field--anchor">
                                  *
                                  <a
                                    target="_blank"
                                    href="/wp-content/themes/veinsa/files/politicas_de_privacidad.pdf"
                                  >
                                    ACEPTO POLÍTICAS DE PRIVACIDAD
                                  </a>{' '}
                                  Y{' '}
                                  <a
                                    target="_blank"
                                    href="/wp-content/themes/veinsa/files/terminos_condiciones_veinsamotors.pdf"
                                  >
                                    TÉRMINOS Y CONDICIONES
                                  </a>
                                </div>
                              </>
                            ),
                            value: 'politica',
                            label: 'Acepto Política de privacidad',
                          },
                        ]}
                      />
                      <div
                        className="classic-form__row classic-form__row--send"
                        style={{ margin: '1.9rem 0' }}
                      >
                        <Recaptcha
                          onChange={(value) => {
                            if (value) {
                              setPassedCaptcha(true);
                            }
                          }}
                        />
                        <Button
                          disabled={
                            !passedCaptcha ||
                            !formik.isValid ||
                            formik.isSubmitting
                          }
                          type="red"
                        >
                          Enviar solicitud
                        </Button>
                      </div>
                    </div>
                    <p className="classic-form__mandatory-legend">
                      * Campos obligatorios
                    </p>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestDrive;
